import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";

import Tooltip from "App/common/ui/Tooltip";
import { TOOLTIPS } from "consts";
import {
  formatBytes,
  forbidExtraProps,
  decodeHandler,
  encodeHandler
} from "utils";
import { useHotKeys } from "App/common/hooks/hotKeys";
import { CONTENT } from "consts";
import { ReactComponent as SafeIcon } from "assets/images/app_svg/safe.svg";
import { ReactComponent as ErrorIcon } from "assets/images/app_svg/error.svg";
import { ReactComponent as EncryptionIcon } from "assets/images/app_svg/encryption.svg";
import { ReactComponent as ComputerIcon } from "assets/images/app_svg/computer.svg";
import { ReactComponent as NewIcon } from "assets/images/app_svg/new-icon.svg";
import { ReactComponent as SliderEditIcon } from "assets/images/app_svg/slider_edit.svg";
import { ReactComponent as CloseIcon } from "assets/images/app_svg/close.svg";
import { SPopupContext } from "App/common/hoc/SlidingPopup";

function ClientDetails(props) {
  const [editComputerName, setEditComputerName] = useState({
    editing: false,
    name: props.clientDetails.name,
    changed: false
  });

  const { sPopupState } = React.useContext(SPopupContext);

  const status = useCallback(() => {
    switch (props.statusKey) {
      case "CANCELLED":
      case "SUCCESS":
      case "PAUSED":
        return (
          <div
            className={[
              props.client.lastBackupTime === 0
                ? "pc_status__enabled"
                : "pc_status__safe",
              "flex_align"
            ].join(" ")}
          >
            <span className="icon">
              {props.client.lastBackupTime === 0 ? (
                <span className="svg_backup_enabled_icon_slider">
                  <NewIcon />
                </span>
              ) : (
                <SafeIcon />
              )}
            </span>
            <span>
              {props.client.lastBackupTime === 0
                ? CONTENT.statuses.backup.new
                : CONTENT.statuses.backup.safe}
            </span>
          </div>
        );
      case "IN_PROGRESS":
        return (
          <div className="pc_status__backingup flex_align">
            <span>{CONTENT.statuses.backup.inProgress}</span>
          </div>
        );
      case "FAILURE":
        return (
          <div className="pc_status__error flex_align">
            <span className="icon">
              <ErrorIcon />
            </span>
            <span>{CONTENT.statuses.backup.error}</span>
          </div>
        );
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.statusKey]);

  function inputComputerNameHandler(changedName) {
    let temp = { ...editComputerName };
    temp.name = changedName;
    if (
      temp.name &&
      temp.name.trim() !== decodeHandler(props.clientDetails.name) &&
      temp.name.trim() !== "" &&
      temp.name.trim().length > 2 &&
      !sPopupState.refresh.show
    ) {
      temp.name = encodeHandler(temp.name);
      temp.changed = true;
    } else {
      temp.changed = false;
    }
    setEditComputerName(temp);
  }

  function toggleEditComputerName() {
    let temp = { ...editComputerName };
    temp.editing = !temp.editing;

    temp.name = decodeHandler(props.clientDetails.name).replace(/\s+/g, " ");

    temp.changed = false;
    setEditComputerName(temp);
  }

  function updateSettings() {
    if (editComputerName.changed) {
      const settings = {
        backupType: props.backupSettings.backupType,
        clientDetails: props.backupSettings.clientDetails,
        log: JSON.stringify({
          mode: "computerName",
          params: { name: editComputerName.name.trim() }
        })
      };
      settings.clientDetails.name = editComputerName.name.trim();
      props.updateBackupSettingsStart(
        settings,
        props.backupSettings.clientDetails.clientUUID
      );
      toggleEditComputerName();
    }
  }

  useHotKeys(
    "Enter",
    e => {
      e.preventDefault();
      updateSettings();
    },
    [editComputerName]
  );

  return (
    <div className="pc_content_wrap">
      <div className="pc_content__img">
        <div className="pc_img">
          <div className="icon">
            <ComputerIcon />
          </div>
        </div>
        <div className="pc_details">
          <div className="pc_name">
            {!editComputerName.editing ? (
              <p>
                {decodeHandler(props.clientDetails.name)}
                {props.client.encryptionType ? (
                  <Tooltip {...TOOLTIPS.backupSettings.encryptionKey}>
                    <div className="encryption">
                      <span className="icon">
                        <EncryptionIcon />
                      </span>
                    </div>
                  </Tooltip>
                ) : null}
                <Tooltip {...TOOLTIPS.backupSettings.edit}>
                  <span
                    className="icon edit_name_icon"
                    data-toggle="tooltip"
                    data-placement="right"
                    data-original-title="Edit"
                    onClick={() => toggleEditComputerName()}
                  >
                    <span className="svg_edit_slider_icon">
                      <SliderEditIcon />
                    </span>
                  </span>
                </Tooltip>
              </p>
            ) : (
              <div className="comp_nameEdit" style={{ display: "block" }}>
                <input
                  type="text"
                  name=""
                  value={decodeHandler(editComputerName.name)}
                  onChange={e => inputComputerNameHandler(e.target.value)}
                  disabled={!editComputerName.editing}
                  maxLength="50"
                  onKeyPress={e => (e.key == "Enter" ? updateSettings() : null)}
                  autoFocus
                />
                <button
                  className={[
                    "btn_primary",
                    editComputerName.changed ? "" : "btn_disable"
                  ].join(" ")}
                  onClick={updateSettings}
                >
                  Save
                </button>
                <span
                  className="icon close_edit_icon"
                  data-toggle="tooltip"
                  data-placement="right"
                  data-original-title="Close"
                  onClick={() => toggleEditComputerName()}
                >
                  <Tooltip {...TOOLTIPS.backupSettings.cancel}>
                    <span className="svg_close_slider_icon">
                      <CloseIcon />
                    </span>
                  </Tooltip>
                </span>
              </div>
            )}
          </div>
          <p>{props.clientDetails.ipAddress}</p>
          <div className="pc_status flex_align">{status()}</div>
        </div>
        {/* <div className="pc_details">
          <div className="pc_name">
            <p>{props.clientDetails.name}</p>
            {props.client.encryptionType ? (
              <Tooltip {...TOOLTIPS.backupSettings.encryptionKey}>
                <div className="encryption">
                  <span className="icon">
                    <EncryptionIcon />
                  </span>
                </div>
              </Tooltip>
            ) : null}
            {props.client.isConfigured && (
              <div className="pc_status flex_align">({status()})</div>
            )}
          </div>
          <p>{props.clientDetails.ipAddress}</p>
        </div> */}
      </div>

      <div className="pc_content__info">
        <p>
          {CONTENT.machineDetails.os}
          <span className="primary_color">
            {" "}
            {props.clientDetails.osPlatform}
          </span>
        </p>
        <p>
          {CONTENT.machineDetails.cpu}
          <span className="primary_color">
            {" "}
            {props.clientDetails.cpuDetail}
          </span>
        </p>
        <p>
          {CONTENT.machineDetails.ram}
          <span className="primary_color">
            {" "}
            {formatBytes(props.clientDetails.ramSize)}
          </span>
        </p>
      </div>
    </div>
  );
}

ClientDetails.propTypes = forbidExtraProps(
  {
    clientDetails: PropTypes.shape({
      clientUUID: function(props, propName, componentName) {
        const matchMe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
        if (!matchMe.test(props[propName])) {
          return new Error(
            "Invalid prop `" +
              propName +
              "` supplied to" +
              " `" +
              componentName +
              "`. Validation failed."
          );
        }
      },
      cpuDetail: PropTypes.string.isRequired,
      ipAddress: function(props, propName, componentName) {
        const matchMe = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!matchMe.test(props[propName])) {
          return new Error(
            "Invalid prop `" +
              propName +
              "` supplied to" +
              " `" +
              componentName +
              "`. Validation failed."
          );
        }
      },
      name: PropTypes.string.isRequired,
      osPlatform: PropTypes.string.isRequired,
      ramSize: PropTypes.string.isRequired
    }).isRequired
  },
  ["client", "updateBackupSettingsStart", "backupSettings"]
).isRequired;

export default ClientDetails;
